import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { useDocumentTitle } from "../lib/useDocumentTitle";
import { PHASES } from "../lib/phases";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { CheckCircle2, Circle, FileText, Lock, Calendar, Mail, Phone, Building2, MessageSquarePlus, Send, X, CheckCheck } from "lucide-react";

const LOGO_URL =
  "https://customer-assets-lqy194kg.emergentagent.net/job_mls-deadline-master/artifacts/jbrp9xqq_equitasfit%20%28Logo%29%20%284%29.png";

const VISIBLE_TASK_IDS = [
  "t6_1", "t6_2",
  "t6_3_a", "t6_3_b", "t6_3_c", "t6_3_e", "t6_3_f", "t6_3_g", "t6_3_h", "t6_3_d",
  "t6_4", "t6_4_vp",
  "t6_5", "t6_6", "t6_7",
];

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(`${iso}T00:00:00`).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch { return ""; }
}

function TaskLine({ task, state, token, myComments, onCommentPosted }) {
  const done = !!state?.done;
  const waived = !!state?.waived;
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const mine = (myComments || []).filter((c) => c.task_id === task.id);

  const submit = async () => {
    const trimmed = text.trim();
    if (!trimmed) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/public/transaction/${token}/comments`, { task_id: task.id, text: trimmed });
      onCommentPosted(data.comment);
      toast.success("Sent to the listing agent");
      setText(""); setOpen(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not send");
    } finally { setBusy(false); }
  };

  return (
    <li className="flex items-start gap-3 py-2.5 border-b border-stone-100 last:border-0" data-testid={`tx-task-${task.id}`}>
      {done ? (
        <CheckCircle2 className="h-4 w-4 mt-1 shrink-0" style={{ color: "#010082" }} />
      ) : (
        <Circle className="h-4 w-4 mt-1 shrink-0 text-stone-300" />
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-start gap-3 flex-wrap">
          <div className="flex-1 min-w-0">
            <p className={`text-sm leading-snug ${done ? "text-stone-500 line-through" : "text-stone-900"}`}>
              {task.label}
              {waived && (
                <span className="ml-2 inline-flex items-center rounded-full bg-amber-100 border border-amber-200 px-2 py-0.5 text-[10px] uppercase tracking-widest text-amber-800 font-semibold">
                  Waived
                </span>
              )}
            </p>
            {state?.date && !done && (
              <p className="text-[11px] text-stone-500 mt-0.5 tabular-nums">Due {formatDate(state.date)}</p>
            )}
            {state?.completed_at && done && (
              <p className="text-[11px] text-stone-400 mt-0.5">Completed {formatDate(state.completed_at.slice(0, 10))}</p>
            )}
          </div>
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            data-testid={`tx-comment-toggle-${task.id}`}
            className="text-[11px] font-semibold uppercase tracking-widest text-stone-500 hover:text-[#010082] flex items-center gap-1"
          >
            {open ? <X className="h-3 w-3" /> : <MessageSquarePlus className="h-3 w-3" />}
            {open ? "Close" : "Ask"}
          </button>
        </div>
        {mine.length > 0 && (
          <ul className="mt-2 space-y-2.5">
            {mine.map((c) => (
              <li key={c.id} className="text-[11px]" data-testid={`tx-my-comment-${c.id}`}>
                <div className="flex items-start gap-1.5 text-stone-600">
                  <CheckCheck className="h-3 w-3 mt-0.5 text-emerald-600 shrink-0" />
                  <span className="leading-relaxed">
                    <span className="text-stone-400 mr-1 uppercase tracking-widest font-semibold">Sent</span>
                    {c.text}
                  </span>
                </div>
                {(c.replies || []).length > 0 && (
                  <ul className="mt-1.5 ml-4 border-l-2 border-[#010082]/25 pl-3 space-y-1.5">
                    {(c.replies || []).map((r) => (
                      <li key={r.id} data-testid={`tx-reply-${r.id}`}>
                        <div className="flex items-baseline gap-1.5 flex-wrap">
                          <span className="text-[10px] uppercase tracking-widest font-semibold text-[#010082]">{r.author_role_label || "Listing Agent"}</span>
                          <span className="text-[10px] text-stone-400">· {r.author_name}</span>
                          <span className="text-[9px] text-stone-400 ml-auto">
                            {new Date(r.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                          </span>
                        </div>
                        <p className="text-xs text-stone-800 whitespace-pre-wrap leading-relaxed mt-0.5">{r.text}</p>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            ))}
          </ul>
        )}
        {open && (
          <div className="mt-2 rounded-md border border-stone-200 bg-stone-50 p-3" data-testid={`tx-comment-form-${task.id}`}>
            <Textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="A quick question or note for the listing agent — only they'll see this."
              rows={2}
              className="text-xs bg-white border-stone-300 leading-relaxed"
              data-testid={`tx-comment-input-${task.id}`}
            />
            <div className="mt-2 flex items-center justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => { setOpen(false); setText(""); }} className="h-8 rounded-full border-stone-300 text-xs">Cancel</Button>
              <Button size="sm" onClick={submit} disabled={busy || !text.trim()} className="h-8 rounded-full text-white text-xs px-4" style={{ backgroundColor: "#010082" }} data-testid={`tx-comment-submit-${task.id}`}>
                <Send className="h-3 w-3 mr-1" />
                {busy ? "Sending…" : "Send"}
              </Button>
            </div>
          </div>
        )}
      </div>
    </li>
  );
}

export default function CollaboratorView() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  useDocumentTitle(data ? `Transaction Hub · ${data.property_address || ""}` : "Transaction Hub");

  useEffect(() => {
    (async () => {
      try {
        const { data: d } = await api.get(`/public/transaction/${token}`);
        setData(d);
      } catch (e) {
        setError(formatApiErrorDetail(e.response?.data?.detail) || "This link is inactive.");
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#faf7f2] flex items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-stone-300 border-t-stone-900" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-[#faf7f2] flex items-center justify-center p-6">
        <div className="rounded-md border border-stone-200 bg-white p-10 text-center max-w-md" data-testid="tx-invalid">
          <Lock className="h-6 w-6 text-stone-400 mx-auto" />
          <h1 className="font-heading text-2xl font-semibold text-stone-900 mt-3">Link unavailable</h1>
          <p className="text-sm text-stone-500 mt-2 leading-relaxed">{error || "This Transaction Hub link is no longer active."}</p>
        </div>
      </div>
    );
  }

  const phase7 = PHASES.find((p) => p.id === "p7");
  const visibleTasks = (phase7?.tasks || []).filter((t) => VISIBLE_TASK_IDS.includes(t.id));

  return (
    <div className="min-h-screen bg-[#faf7f2]" data-testid="transaction-hub-page">
      {/* Neutral header — no 1% List Fee / seller marketing badges */}
      <header className="sticky top-0 z-40 w-full border-b border-stone-200 bg-[#faf7f2]/85 backdrop-blur-md">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-5">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Equitas Homes" className="h-10 w-10 rounded-full object-cover shadow-sm ring-1 ring-stone-200" />
            <div className="leading-tight">
              <span className="overline text-stone-500 block">Equitas Homes</span>
              <span className="text-lg font-heading font-semibold tracking-tight text-stone-900">Transaction Hub</span>
            </div>
          </div>
          <div className="hidden sm:flex items-center gap-2 text-[11px] font-semibold uppercase tracking-widest text-stone-500">
            <span>{data.role_label}</span>
            {data.collaborator_name && <span className="text-stone-300">·</span>}
            <span className="text-stone-700">{data.collaborator_name}</span>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10">
        <div className="mb-10">
          <p className="overline text-stone-500">Transaction in escrow</p>
          <h1 className="font-heading font-semibold tracking-tight text-stone-900 text-4xl sm:text-5xl mt-2">
            {data.property_address || "Property"}
          </h1>
          <p className="text-sm text-stone-600 mt-3 leading-relaxed max-w-2xl">
            Mutual contract milestones only. Pre-listing data stays with the listing side — you'll only see what's shared with your side of the transaction.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <section className="lg:col-span-8">
            <div className="rounded-md border border-stone-200 bg-white p-6" data-testid="tx-milestones">
              <p className="overline text-stone-500 mb-3">Escrow milestones</p>
              <h2 className="font-heading text-2xl font-semibold text-stone-900">Contract to close</h2>
              <ul className="mt-5">
                {visibleTasks.map((task) => (
                  <TaskLine
                    key={task.id}
                    task={task}
                    state={data.tasks?.[task.id]}
                    token={token}
                    myComments={data.my_comments || []}
                    onCommentPosted={(c) => setData((prev) => ({ ...prev, my_comments: [...(prev.my_comments || []), c] }))}
                  />
                ))}
              </ul>
            </div>

            {data.rpa_share_token && (
              <div className="mt-6 rounded-md border border-[#010082]/20 bg-[#010082]/[0.035] p-6" data-testid="tx-rpa-block">
                <p className="overline text-stone-500 mb-2">Ratified Purchase Agreement</p>
                <a
                  href={`${process.env.REACT_APP_BACKEND_URL}/api/public/rpa/${data.rpa_share_token}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="tx-rpa-link"
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-white text-xs font-semibold"
                  style={{ backgroundColor: "#010082" }}
                >
                  <FileText className="h-3.5 w-3.5" />
                  View Ratified Purchase Agreement (PDF)
                </a>
                {data.rpa_filename && (
                  <p className="text-[11px] text-stone-500 mt-2">{data.rpa_filename}</p>
                )}
              </div>
            )}
          </section>

          <aside className="lg:col-span-4 space-y-6">
            <div className="rounded-md border border-stone-200 bg-white p-6" data-testid="tx-key-dates">
              <p className="overline text-stone-500 mb-3">Key dates</p>
              <dl className="space-y-3 text-sm">
                {data.closing_date && (
                  <div>
                    <dt className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold">Close of escrow</dt>
                    <dd className="text-stone-900 flex items-center gap-1.5 mt-0.5"><Calendar className="h-3.5 w-3.5 text-stone-400" /> {formatDate(data.closing_date)}</dd>
                  </div>
                )}
                {data.seller_signing_date && (
                  <div>
                    <dt className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold">Seller signing</dt>
                    <dd className="text-stone-900 flex items-center gap-1.5 mt-0.5"><Calendar className="h-3.5 w-3.5 text-stone-400" /> {formatDate(data.seller_signing_date)}</dd>
                  </div>
                )}
                {data.buyer_names && (
                  <div>
                    <dt className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold">Buyer</dt>
                    <dd className="text-stone-900 mt-0.5">{data.buyer_names}</dd>
                  </div>
                )}
                {data.listing_agent && (
                  <div>
                    <dt className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold">Listing agent</dt>
                    <dd className="text-stone-900 mt-0.5">{data.listing_agent}</dd>
                  </div>
                )}
              </dl>
            </div>

            {(data.escrow_company || data.escrow_officer_name) && (
              <div className="rounded-md border border-stone-200 bg-white p-6" data-testid="tx-escrow-contact">
                <p className="overline text-stone-500 mb-3">Escrow contact</p>
                <dl className="space-y-2 text-sm">
                  {data.escrow_company && (
                    <dd className="flex items-start gap-2 text-stone-900"><Building2 className="h-3.5 w-3.5 text-stone-400 mt-1 shrink-0" /> <span className="font-semibold">{data.escrow_company}</span></dd>
                  )}
                  {data.escrow_officer_name && (
                    <dd className="text-stone-800 pl-5">{data.escrow_officer_name}</dd>
                  )}
                  {data.escrow_officer_email && (
                    <dd className="pl-5"><a href={`mailto:${data.escrow_officer_email}`} className="inline-flex items-center gap-1.5 text-[#010082] hover:underline"><Mail className="h-3 w-3" /> {data.escrow_officer_email}</a></dd>
                  )}
                  {data.escrow_officer_phone && (
                    <dd className="pl-5"><a href={`tel:${data.escrow_officer_phone}`} className="inline-flex items-center gap-1.5 text-[#010082] hover:underline"><Phone className="h-3 w-3" /> {data.escrow_officer_phone}</a></dd>
                  )}
                  {data.escrow_number && (
                    <dd className="text-stone-500 pl-5 font-mono text-xs">Escrow # {data.escrow_number}</dd>
                  )}
                </dl>
              </div>
            )}
          </aside>
        </div>
      </main>
    </div>
  );
}
